import {
  AsYouType,
  getExampleNumber,
  parsePhoneNumber,
} from "libphonenumber-js/max";
import { useEffect, useState } from "react";

import { ChevronDown } from "lucide-react";
import { CountryCode } from "./types";
import { checkPhoneExists } from "@/components/common/utils";
import { cn } from "@/components/common/utils";
import { countries } from "./countries";
import { createClientComponentClient } from "@/utils/supabase/client";
import examples from "libphonenumber-js/mobile/examples";
import { getDefaultCountry } from "./utils";

interface PhoneInputProps {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  variant?: "minimal" | "default";
  onPhoneStatusChange?: (isNewUser: boolean, showTermsAccept: boolean) => void;
  onCountryChange?: (country: CountryCode) => void;
}

const PhoneInput = ({
  phoneNumber,
  setPhoneNumber,
  variant = "default",
  onPhoneStatusChange,
  onCountryChange,
}: PhoneInputProps) => {
  const [inputNumber, setInputNumber] = useState(phoneNumber);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isCountryDirty, setIsCountryDirty] = useState(false);
  const [selectedCountry, setSelectedCountry] =
    useState<CountryCode>(getDefaultCountry());
  const supabase = createClientComponentClient();

  useEffect(() => {
    onPhoneNumberChange(phoneNumber);
  }, [phoneNumber]);

  const onCountrySelect = (country: CountryCode) => {
    setIsCountryDirty(true);
    setSelectedCountry(country);
    setIsCountryDropdownOpen(false);

    onCountryChange?.(country);
    onPhoneNumberChange(phoneNumber);
  };

  const onPhoneNumberChange = async (newNumber: string) => {
    let countryCode: CountryCode | undefined = selectedCountry;
    if (newNumber.startsWith("+")) {
      countryCode = undefined;
    }

    const ayt = new AsYouType(countryCode?.code);
    ayt.input(newNumber);
    if (ayt.isValid()) {
      const country = ayt.getCountry();
      const countryCode = countries.find((c) => c.code === country);

      if (countryCode) {
        setSelectedCountry(countryCode);
        onCountryChange?.(countryCode);
      }

      const parsedNumber = parsePhoneNumber(newNumber, {
        defaultCountry: countryCode?.code,
      });

      setInputNumber(parsedNumber.formatNational());
      setPhoneNumber(parsedNumber.number);
    } else {
      setInputNumber(newNumber);
      setPhoneNumber(newNumber);
    }
    // Run existence check separately if we have enough digits
    try {
      if (newNumber.replace(/\D/g, "").length >= 10) {
        const fullNumber = newNumber.startsWith("+")
          ? newNumber
          : `${selectedCountry.dial_code}${newNumber.replace(/\D/g, "")}`;

        const parsedForCheck = parsePhoneNumber(
          fullNumber,
          selectedCountry.code
        );

        if (parsedForCheck) {
          const strippedNumber = parsedForCheck.number.replace(/^\+/, "");
          const exists = await checkPhoneExists(supabase, strippedNumber);

          const isNewUserFlag = !exists;
          const showTerms = !exists;

          // Notify parent about the updated state
          onPhoneStatusChange?.(isNewUserFlag, showTerms);
        }
      }
    } catch (error) {
      console.error("Error checking phone existence:", error);
    }
  };

  return (
    <div className="flex flex-col gap-2 h-full">
      <label htmlFor="phone" className="sr-only">
        Phone Number
      </label>
      <div className="flex items-stretch h-full">
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
            className={cn(
              "h-full min-w-[90px] px-2 font-sans text-[16px] leading-6 border border-secondary-blue bg-white flex items-center justify-between gap-1 flex-shrink-0",
              "rounded-l-[10px]",
              variant === "minimal" ? "h-[46px]" : "h-[50px]"
            )}
          >
            <span className="flex items-center gap-2">
              <div>{selectedCountry.flag}</div>
              <span className="text-sm">{selectedCountry.dial_code}</span>
            </span>
            <ChevronDown className="w-4 h-4" />
          </button>
          {isCountryDropdownOpen && (
            <div className="absolute z-50 w-72 mt-1 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg max-h-60">
              {countries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className="flex items-center w-full py-1.5 text-sm text-gray-900 hover:bg-gray-100"
                  onClick={() => onCountrySelect(country)}
                >
                  <div className="flex-shrink-0 w-6">{country.flag}</div>
                  <div className="flex-shrink-0 w-12 font-normal text-gray-700">
                    {country.dial_code}
                  </div>
                  <div className="flex-1 truncate text-left text-gray-600 pl-1">
                    {country.name}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <input
          id="phone"
          name="phone"
          type="tel"
          placeholder={
            variant === "minimal"
              ? "Enter your phone number"
              : `e.g. ${
                  getExampleNumber(
                    selectedCountry.code,
                    examples
                  )?.formatNational() ?? "(201) 555-0123"
                }`
          }
          autoComplete="tel"
          required
          aria-required="true"
          onChange={(e) => onPhoneNumberChange(e.target.value)}
          value={inputNumber}
          className={cn(
            "w-full font-sans text-[16px] leading-6 placeholder:text-tertiary-blue border border-secondary-blue",
            "rounded-r-[10px] -ml-px focus:outline-none",
            variant === "minimal"
              ? "h-[46px] px-3 py-2.5"
              : "h-[50px] px-4 py-3"
          )}
        />
      </div>
    </div>
  );
};

export default PhoneInput;
